// components.jsx — shared UI: header, chips, video card, footer
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CATEGORIES, CAT_ICONS, LOCATIONS, VIDEOS, thumbGradient, CURRENT_USER, STATS } from './data';
import { useAuth } from './auth/AuthContext';
import { useContent } from './content/ContentContext';

// ——— Icons (inline SVG) ———
export const Ic = {
  search: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>),
  drone: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="5" cy="5" r="3"/><circle cx="19" cy="5" r="3"/><circle cx="5" cy="19" r="3"/><circle cx="19" cy="19" r="3"/><rect x="9" y="9" width="6" height="6" rx="1"/><path d="M7 7l2 2M17 7l-2 2M7 17l2-2M17 17l-2-2"/></svg>),
  upload: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v14M6 9l6-6 6 6M4 21h16"/></svg>),
  play: (size=14) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 4l14 8-14 8z"/></svg>),
  lock: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="1"/><path d="M8 11V7a4 4 0 018 0v4"/></svg>),
  pin: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z"/><circle cx="12" cy="9" r="2.5"/></svg>),
  eye: () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>),
  heart: () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>),
  fire: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2s4 4 4 8a4 4 0 01-8 0c0-2 2-3 2-5 4 1 6 4 6 7a6 6 0 11-12 0c0-5 8-10 8-10z"/></svg>),
  check: () => (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4 12l6 6L20 6"/></svg>),
  grid: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>),
  menu: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>),
  close: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18"/></svg>),
  chevron: (d='right') => {
    const rot = {right:0,left:180,up:-90,down:90}[d];
    return (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{transform:`rotate(${rot}deg)`}}><path d="M9 6l6 6-6 6"/></svg>);
  },
  star: () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/></svg>),
  crown: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M2 8l5 4 5-7 5 7 5-4-2 12H4z"/></svg>),
  mountain: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 20l5-9 4 6 3-4 6 7z"/><path d="M10 11l-2-3"/></svg>),
  water: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 8c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 2 2"/><path d="M3 14c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 2 2"/></svg>),
  sky: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="17" cy="7" r="3"/><path d="M4 17h12a4 4 0 100-8 5 5 0 00-9.8-1.1A4 4 0 004 17z"/></svg>),
  city: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 21V8l4-3 4 3v13"/><path d="M11 21v-9l5-3 5 3v9"/><path d="M7 11v0M7 15v0M7 19v0M16 13v0M16 17v0"/></svg>),
  heritage: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 10L12 4l9 6"/><path d="M4 10v10h16V10"/><path d="M8 20v-7M12 20v-7M16 20v-7"/></svg>),
  agriculture: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 21V7"/><path d="M12 13c-3 0-5-2-5-4s2-3 5-3M12 13c3 0 5-2 5-4s-2-3-5-3"/><path d="M12 17c-3 0-5-1-5-3M12 17c3 0 5-1 5-3"/></svg>),
  action: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7z"/></svg>),
  wildlife: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="15" r="4"/><circle cx="6.5" cy="9" r="1.8"/><circle cx="17.5" cy="9" r="1.8"/><circle cx="9" cy="5.5" r="1.6"/><circle cx="15" cy="5.5" r="1.6"/></svg>),
  industry: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>),
  patent: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5"/><circle cx="12" cy="14" r="2.5"/><path d="M9.5 16.5L8 21l4-2 4 2-1.5-4.5"/></svg>),
  clipboard: (size=20) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1"/><path d="M9 10h6M9 14h6M9 18h4"/></svg>),
  idCard: (size=20) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="12" r="2.5"/><path d="M14 10h5M14 14h5M5 17c.5-1.5 2-2.5 4-2.5s3.5 1 4 2.5"/></svg>),
  cap: (size=20) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M2 9l10-5 10 5-10 5z"/><path d="M6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5"/><path d="M22 9v6"/></svg>),
  shield: (size=20) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 2L4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5z"/><path d="M9 12l2 2 4-4"/></svg>),
  print3d: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17V9a2 2 0 012-2h14a2 2 0 012 2v8h-3"/><path d="M6 17h12v4H6z"/><path d="M8 7V3h8v4"/><path d="M17.5 11h.01"/></svg>),
  warfare: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5z"/><path d="m9 9 3 3 3-3"/><path d="M12 12v4"/></svg>),
  ai: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4l1.4 3.4L13.8 8.8 10.4 10.2 9 13.6 7.6 10.2 4.2 8.8 7.6 7.4z"/><path d="M17 13l.9 2.2 2.2.9-2.2.9-.9 2.2-.9-2.2-2.2-.9 2.2-.9z"/><path d="M16 4l.6 1.5L18 6.1l-1.4.6L16 8.2l-.6-1.5L14 6.1l1.4-.6z"/></svg>),
  store: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 9h18l-2 11H5z"/><path d="M8 9V5a4 4 0 018 0v4"/></svg>),
};

export function formatViews(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}
export function formatDays(d) {
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.round(d/7)}w ago`;
  if (d < 365) return `${Math.round(d/30)}mo ago`;
  return `${Math.round(d/365)}y ago`;
}

// ——— Search Autocomplete Dropdown ———
export function SearchDropdown({ query, onNav, onSelect, onClose }) {
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState(null); // null = use mock, object = server results
  const q = query.trim().toLowerCase();

  // When DB-backed search is enabled, hit Supabase on query change (debounced).
  useEffect(() => {
    if (!q) { setRemote(null); return; }
    if (import.meta.env.VITE_USE_SUPABASE_DATA !== 'true') { setRemote(null); return; }
    let alive = true;
    const t = setTimeout(async () => {
      const { searchAll } = await import('./db/search');
      const r = await searchAll(q);
      if (alive) setRemote(r);
    }, 150);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const locMatches = q ? (remote?.locations ?? LOCATIONS.filter(l =>
    l.name.toLowerCase().includes(q) || l.country.toLowerCase().includes(q)
  )).slice(0, 5) : [];
  const creatorMatches = q ? (remote?.creators ?? VIDEOS
    .map(v => v.creator).filter(c => c && c.handle).filter((c,i,a) => a.findIndex(x => x.handle === c.handle) === i)
    .filter(c => ((c?.name || '').toLowerCase().includes(q) || (c?.handle || '').toLowerCase().includes(q)))
  ).slice(0, 3) : [];
  const videoMatches = q ? (remote?.videos ?? VIDEOS.filter(v =>
    v.title.toLowerCase().includes(q) || v.tags?.some(t => t.toLowerCase().includes(q))
  )).slice(0, 4) : [];

  const recents = q ? [] : ['Pyramids of Giza', 'Namsan Tower', 'sunrise mountain', 'aerial beach 4K'];
  const trending = q ? [] : [
    { label: 'Golden hour coast', count: '1.2K clips' },
    { label: 'Glacier & ice', count: '892 clips' },
    { label: 'FPV dives', count: '560 clips' },
    { label: 'City skylines', count: '2.4K clips' },
  ];

  const hasResults = locMatches.length + creatorMatches.length + videoMatches.length > 0;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="search-dd" onMouseDown={e => e.preventDefault()}>
      {!q && (
        <>
          <div className="search-dd-section">Recent</div>
          {recents.map((r, i) => (
            <div key={r} className="search-dd-item" onClick={() => { onNav('search'); onClose(); }}>
              <span className="icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></span>
              <span style={{ flex: 1 }}>{r}</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>→</span>
            </div>
          ))}
          <div className="search-dd-section" style={{ marginTop: 8 }}>Trending</div>
          {trending.map(t => (
            <div key={t.label} className="search-dd-item" onClick={() => { onNav('search'); onClose(); }}>
              <span className="icon" style={{ color: 'var(--sunset)' }}><Ic.fire/></span>
              <div style={{ flex: 1 }}>
                <div>{t.label}</div>
                <div style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>{t.count}</div>
              </div>
            </div>
          ))}
        </>
      )}

      {q && !hasResults && (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--parchment-dim)' }}>
          <div style={{ fontSize: 14, marginBottom: 4 }}>No matches for "{query}"</div>
          <div style={{ fontSize: 12 }}>Try a place name, creator handle, or keyword like "sunset" or "coastline"</div>
        </div>
      )}

      {locMatches.length > 0 && (
        <>
          <div className="search-dd-section">Locations</div>
          {locMatches.map(l => (
            <div key={l.id} className="search-dd-item" onClick={() => { onSelect?.({ type: 'location', data: l }); onNav('location', l.id); onClose(); }}>
              <span className="icon" style={{ color: 'var(--amber)' }}><Ic.pin/></span>
              <div style={{ flex: 1 }}>
                <div>{l.name}</div>
                <div style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>{l.country}{l.videos != null ? ` · ${l.videos} clips` : ''}</div>
              </div>
              <span className="kbd">↵</span>
            </div>
          ))}
        </>
      )}

      {creatorMatches.length > 0 && (
        <>
          <div className="search-dd-section">Creators</div>
          {creatorMatches.map(c => (
            <div key={c.handle} className="search-dd-item" onClick={() => { onNav('profile', c.handle.slice(1)); onClose(); }}>
              <span className="icon" style={{ background: 'var(--sunset)', color: '#faf6ec', borderRadius: '50%' }}>{c.name[0]}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {c.name}
                  {c.verified && <span style={{ color: 'var(--amber)' }}><Ic.check/></span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>{c.handle}</div>
              </div>
            </div>
          ))}
        </>
      )}

      {videoMatches.length > 0 && (
        <>
          <div className="search-dd-section">Clips</div>
          {videoMatches.map(v => (
            <div key={v.id} className="search-dd-item" onClick={() => { onSelect?.({ type: 'video', data: v }); onClose(); }}>
              <span className="icon" style={{ width: 48, height: 30, background: thumbGradient(parseInt(v.id.slice(1))), color: '#faf6ec' }}>
                <Ic.play size={11}/>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</div>
                <div style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>{v.creator?.handle || v.channel || '—'} · {v.duration} · {v.resolution}</div>
              </div>
            </div>
          ))}
        </>
      )}

      <div style={{ borderTop: '1px solid var(--line)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--parchment-dim)' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <span><span className="kbd">↑↓</span> navigate</span>
          <span><span className="kbd">↵</span> open</span>
          <span><span className="kbd">esc</span> close</span>
        </div>
        <button onClick={() => { onNav('search'); onClose(); }} style={{ color: 'var(--sunset)', fontWeight: 600 }}>
          See all results →
        </button>
      </div>
    </div>
  );
}

// ——— Header ———
export function Header({ route, onNav, query, setQuery }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const moreRef = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setDdOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  // Cmd+K / Ctrl+K focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setDdOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const txt = useContent;
  // Primary visible (6) — discovery + transactional core
  const primaryLinks = [
    { id: 'home', label: txt('header.nav.map', 'Map') },
    { id: 'explore', label: txt('header.nav.explore', 'Explore') },
    { id: 'commissions', label: txt('header.nav.commissions', 'Commissions') },
    { id: 'rankings', label: txt('header.nav.rankings', 'Rankings') },
    { id: 'atlas', label: txt('header.nav.atlas', 'Atlas') },
    { id: 'creators', label: txt('header.nav.creators', 'Creators') },
  ];
  // Secondary inside "More" dropdown (5) — niche/utility
  const moreLinks = [
    { id: 'lab', label: txt('header.nav.lab', 'Lab') },
    { id: 'gear', label: txt('header.nav.gear', 'Gear') },
    { id: 'live', label: txt('header.nav.live', 'Live') },
    { id: 'shotlibrary', label: txt('header.nav.shots', 'Shots') },
    { id: 'pricing', label: txt('header.nav.pricing', 'Pricing') },
  ];
  const moreActive = moreLinks.some(l => l.id === route);
  const { profile, user, signOut } = useAuth();
  // Prefer real signed-in profile; fall back to mock for guest preview.
  const u = profile ? {
    id: profile.id,
    name: profile.display_name,
    handle: profile.handle,
    email: profile.email,
    initials: (profile.display_name || profile.handle || '?').split(/\s+/).map(s => s[0]).join('').slice(0,2).toUpperCase(),
    pilotVerified: profile.pilot_verified,
    avatarUrl: profile.avatar_url,
  } : { id: '', name: 'Guest', handle: '@guest', initials: '', email: '', pilotVerified: false, avatarUrl: null };
  const isGuest = !user;
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 1000,
      background: 'var(--surface-glass)',
      backdropFilter: 'blur(14px)',
      borderBottom: '1px solid var(--line)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 24,
        padding: '14px 28px', maxWidth: 1760, margin: '0 auto',
      }}>
        <button onClick={() => onNav('home')} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--amber)' }}><Ic.drone/></span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {(() => { const logo = txt('header.logo', 'DroneIcarus'); const m = logo.match(/^[A-Z][a-z]+/); const mid = m ? m[0].length : Math.ceil(logo.length / 2); return (<><span>{logo.slice(0, mid)}</span><span style={{ color: 'var(--sunset)' }}>{logo.slice(mid)}</span></>); })()}
          </span>
          <span className="mono" style={{ fontSize: 12, color: 'var(--parchment-dim)', marginLeft: 4, border: '1px solid var(--line)', padding: '2px 5px', borderRadius: 2 }}>{txt('header.badge', 'BETA')}</span>
        </button>

        <nav style={{ display: 'flex', gap: 4, marginLeft: 12, alignItems: 'stretch' }}>
          {primaryLinks.map(l => (
            <button key={l.id} onClick={() => onNav(l.id)} style={{
              padding: '8px 14px',
              fontSize: 14,
              color: route === l.id ? 'var(--bone)' : 'var(--parchment-dim)',
              borderBottom: route === l.id ? '2px solid var(--amber)' : '2px solid transparent',
              marginBottom: -1,
            }}>{l.label}</button>
          ))}
          <div ref={moreRef} style={{ position: 'relative' }}>
            <button onClick={() => setMoreOpen(v => !v)} style={{
              padding: '8px 14px',
              fontSize: 14,
              color: moreActive ? 'var(--bone)' : 'var(--parchment-dim)',
              borderBottom: moreActive ? '2px solid var(--amber)' : '2px solid transparent',
              marginBottom: -1,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {txt('header.nav.more', 'More')}
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <polyline points="2,4 6,8 10,4"/>
              </svg>
            </button>
            {moreOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1100,
                minWidth: 180, background: '#ffffff',
                border: '1px solid rgba(15,23,42,0.12)',
                borderRadius: 6, boxShadow: '0 12px 40px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
                overflow: 'hidden', padding: '4px 0',
              }}>
                {moreLinks.map(l => (
                  <button key={l.id} onClick={() => { setMoreOpen(false); onNav(l.id); }} style={{
                    display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left',
                    fontSize: 14,
                    color: route === l.id ? 'var(--amber)' : 'var(--bone)',
                    fontWeight: route === l.id ? 600 : 400,
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--forest-900)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div ref={searchRef} style={{ flex: 1, maxWidth: 560, marginLeft: 'auto', position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--forest-900)',
            border: '1px solid ' + (ddOpen ? 'var(--sunset)' : 'var(--line)'),
            borderRadius: 999, padding: '9px 18px',
            transition: 'border-color 0.15s',
            boxShadow: ddOpen ? '0 0 0 3px rgba(200,90,46,0.12)' : 'none',
          }}>
            <span style={{ color: 'var(--parchment-dim)' }}><Ic.search/></span>
            <input
              ref={inputRef}
              value={query}
              onFocus={() => setDdOpen(true)}
              onChange={(e) => { setQuery(e.target.value); setDdOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { onNav('search'); setDdOpen(false); }
                if (e.key === 'Escape') { setDdOpen(false); inputRef.current?.blur(); }
              }}
              placeholder={txt('header.search.placeholder', 'Search place, creator, or keyword — e.g. "Pyramids", "sunrise mountain"')}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--bone)', fontSize: 14, fontFamily: 'inherit',
                boxShadow: 'none',
              }}
            />
            <span className="kbd">{txt('header.search.shortcut', '⌘K')}</span>
          </div>
          {ddOpen && <SearchDropdown query={query} onNav={onNav} onClose={() => setDdOpen(false)} />}
        </div>

        <button onClick={() => onNav('advanced')} title="Advanced filters" style={{
          width: 40, height: 40, borderRadius: 999,
          border: '1px solid var(--line)', background: 'var(--forest-900)',
          color: 'var(--parchment)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="20" y2="12"/><line x1="10" y1="18" x2="20" y2="18"/>
            <circle cx="10" cy="6" r="2" fill="currentColor"/><circle cx="15" cy="12" r="2" fill="currentColor"/><circle cx="7" cy="18" r="2" fill="currentColor"/>
          </svg>
        </button>

        <button onClick={() => onNav('guide')} title={txt('header.btn.guide', 'User guide')} style={{
          width: 40, height: 40, borderRadius: 999,
          border: '1px solid ' + (route === 'guide' ? 'var(--amber)' : 'var(--line)'),
          background: 'var(--forest-900)',
          color: route === 'guide' ? 'var(--amber)' : 'var(--parchment)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.1 9a3 3 0 015.8 1c0 2-3 2.5-3 4"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </button>

        <button onClick={() => onNav('notifications')} title="Notifications" style={{
          position: 'relative',
          width: 40, height: 40, borderRadius: 999,
          border: '1px solid var(--line)', background: 'var(--forest-900)',
          color: 'var(--parchment)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M6 8a6 6 0 0112 0c0 7 3 7 3 9H3c0-2 3-2 3-9z"/><path d="M10 21a2 2 0 004 0"/>
          </svg>
          <span className="badge-dot"/>
        </button>

        {profile?.role === 'admin' && (
          <button onClick={() => onNav('admin')} className="btn" style={{ fontSize: 14, background: 'var(--amber)', color: '#1a2820', fontWeight: 700, letterSpacing: '0.08em' }}>{txt('header.btn.cms', 'CMS')}</button>
        )}
        <button className="btn secondary" onClick={() => onNav('upload')} style={{ fontSize: 14 }}>
          <Ic.upload/> {txt('header.btn.upload', 'Upload')}
        </button>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button onClick={() => isGuest ? onNav('signin') : setMenuOpen(v => !v)} style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'var(--sunset)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: '#faf6ec',
            border: '1px solid var(--line-strong)',
          }}>{isGuest ? 'In' : u.initials}</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', top: 44, right: 0, zIndex: 1100,
              width: 260, background: 'var(--ink)',
              border: '1px solid var(--line-strong)',
              borderRadius: 6, boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden',
            }}>
              <button onClick={() => { setMenuOpen(false); onNav('profile', u.handle.slice(1)); }} style={{
                display: 'flex', gap: 12, alignItems: 'center', width: '100%',
                padding: '14px 16px', borderBottom: '1px solid var(--line)', textAlign: 'left',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: 'var(--sunset)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: '#faf6ec',
                }}>{u.initials}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {u.name}
                    {u.pilotVerified && <span style={{ color: 'var(--amber)' }}><Ic.check/></span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--parchment-dim)' }}>{u.handle} · View profile</div>
                </div>
              </button>
              {(isGuest ? [
                ['signin', 'Sign in / Sign up'],
                ['guidelines', 'Code of conduct'],
                ['legal', 'Licensing & legal'],
              ] : [
                ['mypage', 'My collections'],
                ['messages', 'Messages'],
                ['notifications', 'Notifications'],
                ['orders', 'Orders & licenses'],
                ['creator', 'Creator studio'],
                ['earnings', 'Earnings'],
                ['mystreams', 'My streams'],
                ['livehelp', 'Live help · Go live guide'],
                ['commission', 'Commission a shoot'],
                ['settings', 'Settings'],
                ['pilot-onboarding', 'Become a verified pilot'],
                ['guidelines', 'Code of conduct'],
                ['legal', 'Licensing & legal'],
              ]).map(([id, label]) => (
                <button key={id} onClick={() => { setMenuOpen(false); onNav(id); }} style={{
                  display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left',
                  fontSize: 14, color: 'var(--bone)',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--forest-900)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {label}
                </button>
              ))}
              {!isGuest && (
                <button onClick={async () => { setMenuOpen(false); await signOut(); onNav('home'); }} style={{
                  display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left',
                  fontSize: 14, color: 'var(--sunset)', borderTop: '1px solid var(--line)',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--forest-900)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  Sign out
                </button>
              )}
              <div style={{ borderTop: '1px solid var(--line)', padding: '10px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--parchment-dim)', display: 'flex', justifyContent: 'space-between' }}>
                <span>ID · {u.id}</span>
                <span>KR · ko</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ——— Category Chip Bar ———
export function CategoryChips({ active, onChange, compact = false }) {
  const scrollRef = useRef(null);
  return (
    <div style={{ position: 'relative', padding: compact ? '10px 0' : '16px 0' }}>
      <div ref={scrollRef} style={{
        display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 28px',
        scrollbarWidth: 'none',
      }}>
        {CATEGORIES.map(c => {
          const Icon = CAT_ICONS[c.id];
          return (
            <button key={c.id} onClick={() => onChange(c.id)}
              className={'chip' + (active === c.id ? ' active' : '')}>
              <span className="chip-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>{Icon && Icon(13)}</span>
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ——— Video Card ———
// Safe hash for IDs that may be UUIDs, YouTube ids, or short tokens.
function safeIdHash(id) {
  if (!id) return 0;
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function VideoCard({ video, onClick, size = 'md', showRank = false }) {
  // For YouTube videos there's no location_id; surface country from raw geocoder hint instead.
  const loc = video.locationId
    ? LOCATIONS.find(l => l.id === video.locationId)
    : (video.source === 'youtube' ? { name: video.title?.split(/[—|·]/)[1]?.trim() || video.country || '' } : null);
  const [hover, setHover] = useState(false);
  return (
    <article
      className="video-card fade-in"
      onClick={() => onClick(video)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="video-card-thumb" style={{
        position: 'relative',
        aspectRatio: '16 / 9',
        background: video.thumbUrl ? '#0d1410' : thumbGradient(safeIdHash(video.id)),
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid var(--line)',
      }}>
        {video.thumbUrl ? (
          <img src={video.thumbUrl} alt={video.title || ''}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => {
              // Many ad-blockers / tracking-protection mode in Brave/Firefox/Safari
              // hide i.ytimg.com images. Try a YouTube-Nocookie alias first, then a
              // generic vi.googleusercontent fallback, then hide.
              const img = e.currentTarget;
              const tries = (img.dataset.tries || '0');
              const next = parseInt(tries, 10) + 1;
              img.dataset.tries = String(next);
              const id = (video.youtubeId || video.ytId || '');
              if (next === 1 && id) { img.src = `https://i.ytimg.com/vi_webp/${id}/hqdefault.webp`; return; }
              if (next === 2 && id) { img.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`; return; }
              img.style.display = 'none';
            }} />
        ) : (
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.25 }} viewBox="0 0 400 225" preserveAspectRatio="none">
            <g stroke="rgba(245,237,224,0.4)" fill="none" strokeWidth="0.5">
              {[...Array(6)].map((_, i) => (
                <path key={i} d={`M0 ${40 + i*30} Q100 ${20 + i*30} 200 ${50 + i*30} T400 ${30 + i*30}`}/>
              ))}
            </g>
          </svg>
        )}
        {video.source === 'youtube' && (
          <div style={{
            position: 'absolute', top: 10, left: 10, zIndex: 2,
            fontSize: 12, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em',
            color: '#fff', background: 'rgba(204,0,0,0.92)',
            padding: '3px 7px', borderRadius: 2, fontWeight: 700,
          }}>▶ YOUTUBE</div>
        )}

        {video.price > 0 && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'var(--thumb-overlay)',
              border: '1px solid var(--sunset)',
              padding: '4px 8px', borderRadius: 2,
              fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--sunset)',
            }}><Ic.lock/> ${video.price}</div>
            <div style={{
              fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', fontWeight: 700,
              background: 'var(--amber)', color: '#1a2820',
              padding: '2px 6px', borderRadius: 2,
            }}>DEMO</div>
          </div>
        )}
        {video.price === 0 && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
          }} className="tag-free">FREE</div>
        )}

        <div style={{
          position: 'absolute', bottom: 10, right: 10,
          background: 'var(--thumb-overlay)',
          padding: '3px 7px', borderRadius: 2,
          fontSize: 12, fontFamily: 'var(--font-mono)', color: '#f5ede0',
        }}>{video.duration}</div>

        <div style={{
          position: 'absolute', top: 10, left: 10,
          fontSize: 12, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em',
          color: '#f5ede0', background: 'var(--thumb-overlay)',
          padding: '3px 6px', border: '1px solid var(--line-strong)', borderRadius: 2,
        }}>{video.resolution}</div>

        {showRank && video.rank && (
          <div style={{
            position: 'absolute', bottom: 10, left: 10,
            fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 700,
            color: 'var(--amber)', textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'baseline', gap: 2,
          }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>#</span>{video.rank}
          </div>
        )}

        {/* Hover play overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: hover ? 'rgba(13, 20, 16, 0.4)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s',
          pointerEvents: 'none',
        }}>
          {hover && (
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'var(--sunset)', color: 'var(--bone)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Ic.play size={18}/></div>
          )}
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: 'var(--moss)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: 'var(--bone)',
            border: '1px solid var(--line)',
          }}>{(video.creator?.name || video.channel || 'Y')[0]}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{
              fontSize: 14, fontWeight: 600, marginBottom: 4,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              lineHeight: 1.35, fontFamily: 'var(--font-ui)', letterSpacing: '-0.01em',
            }}>{video.title}</h4>
            <div style={{ fontSize: 14, color: 'var(--parchment-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{video.creator?.handle || video.channel || ''}</span>
              {video.creator?.verified && <span style={{ color: 'var(--amber)' }}><Ic.check/></span>}
            </div>
            <div style={{ fontSize: 14, color: 'var(--parchment-dim)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{ color: 'var(--lichen)' }}><Ic.pin/></span>
              <span>{loc?.name || ''}</span>
              {loc?.name && <span>·</span>}
              <span>{formatViews(video.views)} views</span>
              {video.uploadedDaysAgo > 0 && <><span>·</span><span>{formatDays(video.uploadedDaysAgo)}</span></>}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// ——— Footer ———
const FOOTER_LINK_ROUTES = {
  'Map': 'home',
  'Categories': 'explore',
  'Rankings': 'rankings',
  '’': 'explore', // Editor’s Picks matches by endsWith("Picks")
  'Upload': 'upload',
  'Licensing': 'legal',
  'Payouts': 'earnings',
  'Guidelines': 'guidelines',
  'About': 'legal',
  'Press': 'legal',
  'Careers': 'legal',
  'Contact': 'legal',
  'Terms': 'legal',
  'Privacy': 'privacy',
  'DMCA': 'legal',
  'Airspace': 'legal',
};
function footerRoute(label) {
  if (label.includes('Picks')) return 'explore';
  return FOOTER_LINK_ROUTES[label] || 'home';
}

export function Footer({ onNav }) {
  const txt = useContent;
  const logo = txt('header.logo', 'DroneIcarus');
  const mid = Math.ceil(logo.length / 2);
  // Each footer column's link array is a JSON value in site_content (useContent already parses JSON).
  const cols = [
    { heading: txt('footer.col1.heading', 'Explore'),     links: txt('footer.col1.links', [{label:'Map',route:'home'}]) },
    { heading: txt('footer.col2.heading', 'For pilots'),  links: txt('footer.col2.links', [{label:'Upload a clip',route:'upload'}]) },
    { heading: txt('footer.col3.heading', 'Marketplace'), links: txt('footer.col3.links', [{label:'Pricing',route:'pricing'}]) },
    { heading: txt('footer.col4.heading', 'Company'),     links: txt('footer.col4.links', [{label:'About',route:'legal'}]) },
  ];
  const year = new Date().getFullYear();
  const copyright = String(txt('footer.copyright', '© {year} DroneIcarus, Inc. All footage belongs to its creators.')).replace('{year}', year);
  return (
    <footer style={{
      borderTop: '1px solid var(--line)',
      marginTop: 60,
      padding: '40px 28px 30px',
      background: 'var(--forest-950)',
    }}>
      <div style={{ maxWidth: 1760, margin: '0 auto', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 40 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ color: 'var(--amber)' }}><Ic.drone/></span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600 }}>
              <span>{logo.slice(0, mid)}</span><span style={{ color: 'var(--sunset)' }}>{logo.slice(mid)}</span>
            </span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--parchment-dim)', maxWidth: 360, lineHeight: 1.6 }}>
            {txt('footer.tagline', 'The atlas of aerial footage.')}
          </p>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <span className="mono" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 2, color: 'var(--parchment-dim)' }}>EST. 2026</span>
            <span className="mono" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 2, color: 'var(--parchment-dim)' }}>100+ COUNTRIES</span>
          </div>
        </div>
        {cols.map((col, idx) => (
          <div key={idx}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>{col.heading}</div>
            {(Array.isArray(col.links) ? col.links : []).map((l, i) => (
              <button key={i} onClick={() => onNav?.(footerRoute(l.label) || l.route || 'home')} style={{ display: 'block', background: 'none', border: 'none', padding: 0, fontSize: 14, color: 'var(--parchment)', marginBottom: 8, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>{l.label}</button>
            ))}
          </div>
        ))}
      </div>
      <div style={{
        maxWidth: 1760, margin: '30px auto 0', paddingTop: 20,
        borderTop: '1px solid var(--line)',
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--parchment-dim)',
      }}>
        <span>{copyright}</span>
        <span className="mono">
          <a href={`mailto:${txt('footer.contact_email', 'droneicarusadmin@gmail.com')}`}
             style={{ color: 'var(--parchment-dim)', textDecoration: 'none' }}
             onMouseEnter={e => e.currentTarget.style.color = 'var(--amber)'}
             onMouseLeave={e => e.currentTarget.style.color = 'var(--parchment-dim)'}>
            {txt('footer.contact_email', 'droneicarusadmin@gmail.com')}
          </a>
          <span style={{ margin: '0 10px', opacity: 0.5 }}>·</span>
          Seoul HQ
        </span>
      </div>
    </footer>
  );
}

// Reusable follow button — wires real Supabase follows for UUID profiles,
// otherwise just toggles local UI + toast (so demo creators give feedback).
export function FollowButton({ creatorId, creatorHandle, className = 'btn', style }) {
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const isUuid = typeof creatorId === 'string' && /^[0-9a-f]{8}-/.test(creatorId);

  useEffect(() => {
    if (!isUuid) return;
    import('./db/social').then(({ isFollowing }) => isFollowing(creatorId).then(setFollowing).catch(() => {}));
  }, [creatorId, isUuid]);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isUuid) {
        const { toggleFollow } = await import('./db/social');
        const now = await toggleFollow(creatorId);
        setFollowing(now);
        (window.toast || (() => {}))(now ? 'Following' : 'Unfollowed', creatorHandle || '', 'success');
      } else {
        // Mock creator — toggle UI, friendly toast
        setFollowing(v => !v);
        (window.toast || (() => {}))(following ? 'Unfollowed' : 'Following', (creatorHandle || 'pilot') + ' (demo)', 'info');
      }
    } catch (e) {
      (window.toast || (() => {}))('Sign in to follow', e.message || '', 'error');
    } finally { setBusy(false); }
  };

  return (
    <button className={className + (following ? ' secondary' : '')} style={style} onClick={onClick} disabled={busy}>
      {following ? 'Following' : 'Follow'}
    </button>
  );
}



// ─────────────────────────────────────────────────────────────
// ScrollToTop floating button — auto-shows when window.scrollY > threshold
// ─────────────────────────────────────────────────────────────
export function ScrollToTop({ threshold = 400 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  const goTop = () => {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch(e) {}
    // Some browsers / scroll containers ignore smooth — fall back after 600ms
    setTimeout(() => { if (window.scrollY > 0) { document.documentElement.scrollTop = 0; document.body.scrollTop = 0; } }, 600);
  };
  return (
    <button
      className={'scroll-top-btn' + (visible ? ' is-visible' : '')}
      onClick={goTop}
      aria-label="Scroll to top"
      title="Scroll to top"
      tabIndex={visible ? 0 : -1}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Pagination (numbered) + usePagination hook
// ─────────────────────────────────────────────────────────────
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [];
  const startWin = Math.max(2, current - 1);
  const endWin = Math.min(total - 1, current + 1);
  out.push(1);
  if (startWin > 2) out.push('…');
  for (let i = startWin; i <= endWin; i++) out.push(i);
  if (endWin < total - 1) out.push('…');
  out.push(total);
  return out;
}

export function Pagination({ page, pageCount, onChange, totalItems, pageSize, scrollTargetId }) {
  const pages = useMemo(() => buildPageList(page, pageCount || 1), [page, pageCount]);
  if (!pageCount || pageCount <= 1) return null;
  const go = (p) => {
    if (p < 1 || p > pageCount || p === page) return;
    onChange(p);
    setTimeout(() => {
      const el = scrollTargetId ? document.getElementById(scrollTargetId) : null;
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 30);
  };
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems || page * pageSize);
  return (
    <nav className="pagination" aria-label="Pagination">
      <button className="pagination-btn pagination-arrow" disabled={page <= 1} onClick={() => go(page - 1)} aria-label="Previous page">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Prev
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={'e'+i} className="pagination-ellipsis" aria-hidden="true">…</span>
        ) : (
          <button
            key={p}
            className={'pagination-btn' + (p === page ? ' is-active' : '')}
            onClick={() => go(p)}
            aria-current={p === page ? 'page' : undefined}
            aria-label={'Page ' + p}
          >{p}</button>
        )
      )}
      <button className="pagination-btn pagination-arrow" disabled={page >= pageCount} onClick={() => go(page + 1)} aria-label="Next page">
        Next
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {totalItems != null && (
        <div className="pagination-meta">{start}–{end} of {totalItems}</div>
      )}
    </nav>
  );
}

// Slice an array into pages, reset to 1 when total changes (filters apply).
export function usePagination(items, pageSize = 24) {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [total]);
  useEffect(() => { if (page > pageCount) setPage(1); }, [page, pageCount]);
  const slice = useMemo(() => {
    if (!items) return [];
    const s = (page - 1) * pageSize;
    return items.slice(s, s + pageSize);
  }, [items, page, pageSize]);
  return { page, setPage, pageCount, slice, total, pageSize };
}
